import { useState, useRef, useCallback } from "react";
import { SiFacebook, SiX } from "react-icons/si";
import { FaLinkedinIn } from "react-icons/fa";
import { Link2 } from "lucide-react";
import { openLink, hapticTap } from "@/tg/init";
import { useI18n } from "@/i18n";
import CopyToast from "./CopyToast";

interface ShareBlockProps {
  link: string;
  title: string;
  label?: string;
}

export default function ShareBlock({ link, title, label }: ShareBlockProps) {
  const { t } = useI18n();
  const [toastVisible, setToastVisible] = useState(false);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = useCallback(() => {
    hapticTap();
    navigator.clipboard.writeText(link).catch(() => {});
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToastVisible(true);
    toastTimerRef.current = setTimeout(() => setToastVisible(false), 2000);
  }, [link]);

  const btnStyle: React.CSSProperties = {
    width: 48,
    height: 48,
    borderRadius: "50%",
    background: "var(--tint)",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    WebkitTapHighlightColor: "transparent",
    flexShrink: 0,
  };

  return (
    <section style={{ marginTop: 32 }}>
      <p
        style={{
          fontSize: 16,
          fontWeight: 600,
          fontFamily: '"e-UkraineHead", system-ui, sans-serif',
          color: "var(--ink)",
          margin: "0 0 12px",
        }}
      >
        {label ?? t("share_news")}
      </p>

      <div style={{ display: "flex", gap: 12 }}>
        <button
          style={btnStyle}
          aria-label="Share on Facebook"
          onClick={() => {
            hapticTap();
            openLink("https://www.facebook.com/sharer/sharer.php?u=" + encodeURIComponent(link));
          }}
        >
          <SiFacebook size={22} color="var(--ink)" />
        </button>

        <button
          style={btnStyle}
          aria-label="Share on LinkedIn"
          onClick={() => {
            hapticTap();
            openLink("https://www.linkedin.com/sharing/share-offsite/?url=" + encodeURIComponent(link));
          }}
        >
          <FaLinkedinIn size={22} color="var(--ink)" />
        </button>

        <button
          style={btnStyle}
          aria-label="Share on X"
          onClick={() => {
            hapticTap();
            openLink(
              "https://twitter.com/intent/tweet?url=" +
                encodeURIComponent(link) +
                "&text=" +
                encodeURIComponent(title)
            );
          }}
        >
          <SiX size={22} color="var(--ink)" />
        </button>

        <button
          style={btnStyle}
          aria-label={t("copy_link")}
          onClick={handleCopy}
        >
          <Link2 size={22} color="var(--ink)" />
        </button>
      </div>

      <CopyToast message={t("copied")} show={toastVisible} />
    </section>
  );
}
